import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import { queryOptions } from "@tanstack/react-query";
import type {
  ChatConversation,
  ChatMessage as DbChatMessage,
} from "@/types/assistant.types";
import type { Message, Thread } from "../types";
import { assistantFetchJson, getAccessToken } from "../lib/api";

type AssistantListConversationsResponse = {
  conversations: ChatConversation[];
  limit: number;
  offset: number;
};

type AssistantConversationWithMessagesResponse = {
  conversation: ChatConversation;
  messages: DbChatMessage[];
};

type AssistantSuggestionsResponse = {
  suggestions: string[];
};

function previewMessage(content: string): string {
  return content.slice(0, 55) + (content.length > 55 ? "..." : "");
}

function formatThreadTimestamp(value: string): string {
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
  });
}

function formatMessageTimestamp(value: string): string {
  return new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mapConversationToThread(
  conversation: ChatConversation,
  messages: DbChatMessage[] = [],
): Thread {
  const lastMessage = messages.at(-1);
  const timestamp = conversation.record_updated ?? conversation.record_created;

  return {
    id: conversation.conversation_id,
    name: conversation.title || "Analysis Chat",
    lastMessage: lastMessage
      ? previewMessage(lastMessage.content)
      : "No messages yet",
    timestamp: formatThreadTimestamp(timestamp),
  };
}

function mapMessage(message: DbChatMessage): Message {
  return {
    id: message.message_id,
    role: message.role,
    content: message.content,
    timestamp: formatMessageTimestamp(message.record_created),
  };
}

async function fetchConversationDetail(
  conversationId: string,
  accessToken?: string,
): Promise<AssistantConversationWithMessagesResponse> {
  const token = accessToken ?? (await getAccessToken());
  return assistantFetchJson<AssistantConversationWithMessagesResponse>(
    `/conversations/${encodeURIComponent(conversationId)}`,
    token,
  );
}

async function fetchConversationThreads(analysisId: string): Promise<Thread[]> {
  const accessToken = await getAccessToken();
  const result = await assistantFetchJson<AssistantListConversationsResponse>(
    `/analysis/${encodeURIComponent(analysisId)}/conversations`,
    accessToken,
  );

  return await Promise.all(
    result.conversations.map(async (conversation) => {
      try {
        const details = await fetchConversationDetail(
          conversation.conversation_id,
          accessToken,
        );
        return mapConversationToThread(details.conversation, details.messages);
      } catch (error) {
        console.error("Failed to load conversation preview:", error);
        return mapConversationToThread(conversation);
      }
    }),
  );
}

async function fetchConversationMessages(
  conversationId: string,
): Promise<Message[]> {
  const result = await fetchConversationDetail(conversationId);
  return result.messages.map(mapMessage);
}

async function fetchConversationSuggestions(
  conversationId: string,
): Promise<string[]> {
  const accessToken = await getAccessToken();
  const result = await assistantFetchJson<AssistantSuggestionsResponse>(
    `/conversations/${encodeURIComponent(conversationId)}/suggestions`,
    accessToken,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );

  return result.suggestions;
}

export const assistantQueries = {
  all: getKeyOnlyQueryConfig(["assistant"]),

  conversations: {
    all: () => getKeyOnlyQueryConfig(["assistant", "conversations"]),

    lists: () => getKeyOnlyQueryConfig(["assistant", "conversations", "list"]),

    list: (analysisId: string) =>
      queryOptions({
        queryKey: [
          ...assistantQueries.conversations.lists().queryKey,
          analysisId,
        ],
        queryFn: () => fetchConversationThreads(analysisId),
        enabled: Boolean(analysisId),
      }),

    details: () =>
      getKeyOnlyQueryConfig(["assistant", "conversations", "detail"]),

    detail: (conversationId: string) =>
      queryOptions({
        queryKey: [
          ...assistantQueries.conversations.details().queryKey,
          conversationId,
        ],
        queryFn: () => fetchConversationMessages(conversationId),
        enabled: Boolean(conversationId) && !conversationId.startsWith("temp-"),
      }),
  },

  suggestions: {
    all: () => getKeyOnlyQueryConfig(["assistant", "suggestions"]),

    list: (conversationId: string, enabled = true) =>
      queryOptions({
        queryKey: [
          ...assistantQueries.suggestions.all().queryKey,
          conversationId,
        ],
        queryFn: () => fetchConversationSuggestions(conversationId),
        enabled:
          enabled &&
          Boolean(conversationId) &&
          !conversationId.startsWith("temp-"),
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
      }),
  },
};

export {
  fetchConversationDetail,
  fetchConversationMessages,
  fetchConversationSuggestions,
  fetchConversationThreads,
  mapConversationToThread,
};
